import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useBuilding } from '../../contexts/BuildingContext';
import { LuMenu, LuBuilding2, LuChevronDown, LuLogOut, LuShield, LuLanguages } from 'react-icons/lu';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import { getRoleLabel, getRoleBadgeColor } from '../../utils/roleCheck';

export default function Header({ onMenuClick }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { currentBuilding, currentProperty, availableBuildings, availableProperties, switchBuilding, switchProperty } = useBuilding();
  const [showBuildingMenu, setShowBuildingMenu] = useState(false);
  const [showPropertyMenu, setShowPropertyMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const canSwitchBuildings = availableBuildings.length > 0;
  const canSwitchProperties = availableProperties.length > 1;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageMenu(false);
  };

  const languages = {
    en: 'English',
    el: 'Ελληνικά'
  };

  // Filter and enrich buildings for the selector
  const buildingsForSelector = useMemo(() => {
    if (!currentProperty) return [];

    // Virtual options
    const options = [
    { id: 'all', name: t('common.all'), isVirtual: true },
    { id: 'property', name: t('faultReports.propertyWide'), isVirtual: true }];


    // Real buildings for this property
    const filtered = availableBuildings.filter((b) => b.property_id === currentProperty.id);
    return [...options, ...filtered];
  }, [availableBuildings, currentProperty, t]);

  const displayBuildingName = useMemo(() => {
    if (!currentBuilding) return '';
    if (currentBuilding.isAll) return t('common.all');
    if (currentBuilding.isPropertyWide) return t('faultReports.propertyWide');
    return currentBuilding.name;
  }, [currentBuilding, t]);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition">
            <LuMenu className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-2">
            <LuBuilding2 className="w-6 h-6 text-blue-600" />
            <span className="font-semibold text-gray-900 hidden sm:inline">{t('header.appName')}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Role Badge - Visible on larger screens */}
          {user?.role &&
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-white">
              <LuShield className="w-4 h-4 text-gray-500" />
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${getRoleBadgeColor(user.role)}`}>
                {getRoleLabel(user.role)}
              </span>
            </div>
          }

          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition"
              title={t('header.language')}>
              <LuLanguages className="w-5 h-5 text-gray-700" />
              <span className="text-sm font-medium text-gray-700 hidden md:inline">
                {languages[i18n.language] || 'English'}
              </span>
            </button>
            
            {showLanguageMenu &&
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {Object.entries(languages).map(([code, name]) =>
              <button
                key={code}
                onClick={() => changeLanguage(code)}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition ${
                i18n.language === code ? 'bg-blue-50 text-blue-600 font-medium' : ''}`
                }>
                    {name}
                  </button>
              )}
              </div>
            }
          </div>

          {/* Notifications */}
          <NotificationDropdown />

          {/* Property Selector */}
          {canSwitchProperties && currentProperty &&
          <div className="relative">
              <button
              onClick={() => setShowPropertyMenu(!showPropertyMenu)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-sm">
                <span className="font-medium text-gray-700 hidden sm:inline">{currentProperty.name}</span>
                <span className="font-medium text-gray-700 sm:hidden">{t('header.property')}</span>
                <LuChevronDown className="w-4 h-4" />
              </button>
              
              {showPropertyMenu &&
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {availableProperties.map((prop) =>
              <button
                key={prop.id}
                onClick={() => {
                  switchProperty(prop.id);
                  setShowPropertyMenu(false);
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition ${
                currentProperty?.id === prop.id ? 'bg-blue-50 text-blue-600' : ''}`
                }>
                      <div className="font-medium">{prop.name}</div>
                      <div className="text-xs text-gray-500 truncate">{prop.address}</div>
                    </button>
              )}
                </div>
            }
            </div>
          }

          {/* Building Selector */}
          {canSwitchBuildings && currentBuilding &&
          <div className="relative">
              <button
              onClick={() => setShowBuildingMenu(!showBuildingMenu)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition text-sm">
                <span className="font-medium hidden sm:inline">{displayBuildingName}</span>
                <span className="font-medium sm:hidden">{t('header.building')}</span>
                <LuChevronDown className="w-4 h-4" />
              </button>
              
              {showBuildingMenu &&
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {buildingsForSelector.map((building) =>
              <button
                key={building.id}
                onClick={() => {
                  switchBuilding(building.id);
                  setShowBuildingMenu(false);
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition ${
                currentBuilding?.id === building.id ? 'bg-blue-50 text-blue-600 font-bold' : ''}`
                }>
                      <div className="font-medium">{building.isVirtual ? building.name : building.name}</div>
                      {!building.isVirtual && <div className="text-xs text-gray-500">{building.code}</div>}
                    </button>
              )}
                </div>
            }
            </div>
          }

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <LuChevronDown className="w-4 h-4 hidden sm:inline" />
            </button>

            {showUserMenu &&
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="font-medium text-sm">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                  <div className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full border mt-2 ${getRoleBadgeColor(user?.role)}`}>
                    {getRoleLabel(user?.role)}
                  </div>
                </div>
                <button
                onClick={logout}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 transition text-red-600 flex items-center gap-2">
                  <LuLogOut className="w-4 h-4" />
                  <span>{t('header.logout')}</span>
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    </header>);

}